// Renewal brief — route: /admin/insights/renewal-brief
//
// A standalone, print-friendly DOCUMENT (not a dashboard tab) that assembles
// the renewal argument for one organization from data the insights dashboard
// already computes:
//
//   1. Produced value   — district standards mastered this year (impact series)
//   2. What's renewing   — subscription cohorts by end date (robot util)
//   3. Idle capacity     — per-robot lessons vs the fleet's own FLOOR
//
// Framing decisions (argued with Evan, 2026-07):
//   - Idle/low-use is measured RELATIVE to the district's own fleet, never
//     against our 2-lessons/week recommendation. Benchmarking against the
//     recommendation makes every district look under-target and argues against
//     renewing OR buying more — the opposite of the brief's job.
//   - The benchmark is a conservative FLOOR (25th percentile of robots with
//     any use — "the level 3 in 4 of your robots already clear"), not the
//     median, so the "capacity left on the table" claim is defensible.
//   - Per-robot data is LESSONS delivered (mastery is not attributed per robot
//     — see the deferred robot-attribution decision). The lesson gap is bridged
//     to standards using the district's own mastered-per-lesson rate, clearly
//     labelled as such.
//   - No pricing/currency anywhere — outcomes are the currency by design.
//
// This is a thin composition layer: it loads its own data (so it is directly
// linkable / printable / shareable) but reuses the same endpoints, helpers, and
// palette as the dashboard. Superadmins scope with ?organization=; the year
// follows ?start= like the rest of the dashboard.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Line, Bar } from "react-chartjs-2";
import "chart.js/auto";
import FA from "../fa";
import withAPI from "../../services/api";
import { useTitle } from "../../common/utils";
import MainDashboard from "../../partials/Main";
import { INK, MUTED, GRID, STATUS, UTIL_STATES } from "../impact/tokens";
import { buildSeries, subjSum, median } from "../impact/model";
import { syLabel, syWindow, RECONSTRUCTED_NOTE } from "../impact/schoolyear";
import { fmtBucketLabel, fmtMonth } from "../impact/format";
import { YearSelect, OrgScopeSelect } from "../impact/chrome";
import { CoverageNote } from "./chrome";
import { isExpired } from "../impact/fleet";
import { useDashboardScope } from "./scope";

// Tier palette reuses the validated fleet states so the brief reads as part of
// the same system: green = healthy, amber = warning, gray = absence of signal (an
// idle robot produced nothing). Taken from tokens.js, where the palette is
// defined -- import it from there rather than through a re-exporting consumer.
const TIER = {
  well: { color: UTIL_STATES.active.color, label: "Clears the floor" },
  low: { color: UTIL_STATES.connected.color, label: "Below the floor" },
  idle: { color: UTIL_STATES.dark.color, label: "Idle" },
};
const VALUE_COLOR = STATUS.good.color; // produced-value accent (mastery)

const percentile = (ascArr, p) => {
  if (!ascArr.length) return 0;
  const idx = (p / 100) * (ascArr.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return ascArr[lo];
  return ascArr[lo] + (ascArr[hi] - ascArr[lo]) * (idx - lo);
};

const fmt = (n) => (n == null ? "—" : Math.round(n).toLocaleString());

// Produced value from the district impact window. buildSeries drives the
// chart; the ABii-assisted split (eventually_mastered) isn't in its output, so
// we compute it from the same rebased window (last boundary minus the trimmed
// baseline) to stay consistent with masteredInWindow.
const producedValue = (win) => {
  const series = buildSeries(win);
  if (!series || !series.length) return null;
  const all = win.subsets.all;
  const lc = win.learner_counts || [];
  const first = all.findIndex((_, i) => (lc[i] || 0) > 0);
  const baseIdx = first > 0 ? first : 0;
  const last = all.length - 1;
  const assisted =
    subjSum(all[last], ["eventually_mastered"]) -
    subjSum(all[baseIdx], ["eventually_mastered"]);
  const end = series[series.length - 1];
  return {
    series,
    mastered: end.masteredInWindow,
    assisted: Math.max(0, assisted),
    learners: end.learnersInWindow,
  };
};

// Fleet capacity model built entirely from within-district figures.
const fleetCapacity = (robots) => {
  if (!robots || !robots.length) return null;
  const lessonsTotal = robots.reduce((a, r) => a + (r.lessons_total || 0), 0);
  const activePool = robots
    .map((r) => r.lessons_total || 0)
    .filter((v) => v > 0)
    .sort((a, b) => a - b);
  if (!activePool.length) return { lessonsTotal, degenerate: true };

  const floor = Math.round(percentile(activePool, 25));
  const medianLessons = Math.round(median(activePool));
  const tierOf = (v) => (v <= 0 ? "idle" : v < floor ? "low" : "well");
  const counts = { well: 0, low: 0, idle: 0 };
  robots.forEach((r) => counts[tierOf(r.lessons_total || 0)]++);

  const below = robots.filter((r) => (r.lessons_total || 0) < floor);
  const underSum = below.reduce((a, r) => a + (r.lessons_total || 0), 0);
  const lessonGap = below.length * floor - underSum;
  const rate = lessonsTotal > 0 ? null : 0; // set by caller (needs mastered)

  return {
    lessonsTotal,
    floor,
    medianLessons,
    tierOf,
    counts,
    underCount: below.length,
    underSum,
    lessonGap,
    rate,
    sorted: [...robots].sort(
      (a, b) => (b.lessons_total || 0) - (a.lessons_total || 0)
    ),
  };
};

// Subscription cohorts by end month (mirrors the Robots tab), split into the
// soonest-expiring and later groups for the brief's "what's renewing" section.
const buildCohorts = (robots) => {
  const now = new Date();
  const nowMonth = now.toISOString().slice(0, 7);
  const withSubs = robots.filter((r) => r.sub_end_date);
  const map = new Map();
  withSubs.forEach((r) => {
    const key = r.sub_end_date.slice(0, 7);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });
  const cohorts = [...map.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([key, rs]) => {
      const days = Math.round(
        (new Date(`${key}-01`) - now) / 86400000
      );
      return {
        key,
        label: fmtMonth(`${key}-01`),
        n: rs.length,
        learners: rs.reduce((a, r) => a + (r.unique_learners || 0), 0),
        expired: key < nowMonth,
        soon: key >= nowMonth && days <= 90,
      };
    });
  return { cohorts, totalSubs: withSubs.length, noSub: robots.length - withSubs.length };
};

// ---- small presentational pieces -------------------------------------------
const card = {
  border: `1px solid ${GRID}`,
  borderRadius: 14,
  padding: 20,
  background: "#fff",
};
const eyebrow = {
  fontFamily:
    "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: MUTED,
};
const tnum = { fontVariantNumeric: "tabular-nums" };

const MoveHead = ({ num, title, sub }) => (
  <div style={{ marginBottom: 18 }}>
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 12,
        paddingBottom: 10,
        borderBottom: `1px solid ${GRID}`,
      }}
    >
      <span style={{ ...eyebrow, color: VALUE_COLOR, fontWeight: 700 }}>
        {num}
      </span>
      <h2 style={{ margin: 0, fontSize: 20, color: INK }}>{title}</h2>
    </div>
    {sub && (
      <p style={{ color: "#52514e", fontSize: 14, margin: "12px 0 0", maxWidth: "68ch" }}>
        {sub}
      </p>
    )}
  </div>
);

const Kpi = ({ label, value, foot, color }) => (
  <div style={card}>
    <div style={{ ...eyebrow, marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 30, fontWeight: 800, color: color || INK, ...tnum, lineHeight: 1 }}>
      {value}
    </div>
    {foot && <div style={{ fontSize: 12, color: MUTED, marginTop: 7 }}>{foot}</div>}
  </div>
);

// horizontal reference lines (floor solid, median dashed) for the skyline
const refLinesPlugin = (floor, medianLessons) => ({
  id: "refLines",
  afterDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const y = scales.y;
    if (!y) return;
    const draw = (val, color, dash, label) => {
      if (val == null || val <= 0) return;
      const py = y.getPixelForValue(val);
      if (py < chartArea.top || py > chartArea.bottom) return;
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = dash ? 1 : 1.5;
      ctx.setLineDash(dash || []);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, py);
      ctx.lineTo(chartArea.right, py);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = color;
      ctx.font = "600 10px ui-monospace, Menlo, monospace";
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
      ctx.fillText(label, chartArea.right - 2, py - 3);
      ctx.restore();
    };
    draw(medianLessons, MUTED, [4, 4], `Typical robot · ${medianLessons}`);
    draw(floor, INK, null, `Floor · ${floor} · ¾ of fleet clears this`);
  },
});

const RenewalBrief = ({ api, user, history, location }) => {
  useTitle("Renewal brief");
  const [state, setState] = useState({ loading: true });

  // Organization, school year and roster provenance -- the same hook the
  // dashboard uses, so the two can't drift apart. See ./scope.
  const {
    isSuperadmin,
    selectedOrg,
    selectOrg: handleSelectOrg,
    org,
    today,
    curSY,
    schoolYear,
    selectSchoolYear,
    yearOptions,
    coverage,
    noteLedger,
    resetCoverage,
  } = useDashboardScope({ api, user, history });

  // Always a whole month-bucketed school year: this is a document, with none of
  // the dashboard's interval or end-date overrides.
  const windowSpec = useMemo(
    () => ({ ...syWindow(schoolYear, curSY, today), interval: "month" }),
    [schoolYear, curSY, today]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setState({ loading: true });
      resetCoverage();
      if (!org || !org.id) {
        if (!cancelled) setState({ loading: false, awaitingOrg: isSuperadmin });
        return;
      }
      try {
        const { start, end, interval } = windowSpec;
        const [impact, util] = await Promise.all([
          api
            .fetchWindowedImpactStats({ scope: "organization", id: org.id, start, end, interval })
            .catch(() => null),
          api
            .fetchWindowedRobotUtilization({ scope: "organization", id: org.id, start, end, interval })
            .catch(() => null),
        ]);
        if (!cancelled) {
          [impact, util].forEach(noteLedger);
          setState({ loading: false, impact, util });
        }
      } catch (err) {
        console.error("Renewal brief load failed:", err);
        if (!cancelled) setState({ loading: false, error: String(err) });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolYear, selectedOrg && selectedOrg.id]);

  const { loading, error, awaitingOrg, impact, util } = state;

  // in-service fleet = owned minus lapsed subscriptions (the brief argues to
  // renew, so expired robots are the SUBJECT of renewal, but the produced-value
  // and capacity figures should reflect the fleet that was actually in service)
  const robots = useMemo(
    () => (util && util.robots ? util.robots.filter((r) => !isExpired(r)) : []),
    [util]
  );
  const pv = useMemo(() => (impact ? producedValue(impact) : null), [impact]);
  const cap = useMemo(() => {
    const c = fleetCapacity(robots);
    if (c && !c.degenerate && pv && c.lessonsTotal > 0) {
      c.rate = pv.mastered / c.lessonsTotal;
      c.standardsGap = Math.round((c.lessonGap * c.rate) / 50) * 50;
    }
    return c;
  }, [robots, pv]);
  const renewal = useMemo(
    () => (util && util.robots ? buildCohorts(util.robots) : null),
    [util]
  );

  // idle/low robots by school (for the redeploy recommendation)
  const idleBySchool = useMemo(() => {
    if (!cap || cap.degenerate) return [];
    const m = new Map();
    robots.forEach((r) => {
      if ((r.lessons_total || 0) >= cap.floor) return;
      const ous = r.orgunits && r.orgunits.length ? r.orgunits : [{ name: "Unassigned" }];
      ous.forEach((ou) => m.set(ou.name, (m.get(ou.name) || 0) + 1));
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [robots, cap]);

  const orgName = (org && org.name) || "Your district";
  const search = location.search || "";

  const controls = (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap",
        // keep controls hugging the right even when they wrap to their own line
        // (parent is space-between, which would otherwise left-align a lone
        // wrapped row and push the org picker's dropdown under the sidebar)
        marginLeft: "auto",
        justifyContent: "flex-end",
      }}
      className="rb-no-print"
    >
      {isSuperadmin && <OrgScopeSelect api={api} value={selectedOrg} onChange={handleSelectOrg} />}
      <YearSelect
        year={schoolYear}
        yearOptions={yearOptions}
        onYearChange={selectSchoolYear}
      />
      <button
        onClick={() => window.print()}
        style={{
          border: `1px solid ${GRID}`,
          background: "#fff",
          color: INK,
          borderRadius: 8,
          padding: "8px 14px",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <FA icon="print" /> Print / save PDF
      </button>
    </div>
  );

  let body;
  if (awaitingOrg) {
    body = <div style={{ color: "#52514e" }}>Select an organization above to generate its renewal brief.</div>;
  } else if (loading) {
    body = (
      <div>
        <FA color="gray" icon="spinner" spin /> Assembling renewal brief…
      </div>
    );
  } else if (error) {
    body = <div style={{ color: STATUS.critical.color }}>Failed to load: {error}</div>;
  } else if (!pv && (!cap || cap.degenerate)) {
    body = (
      <div style={{ color: "#52514e", maxWidth: "68ch" }}>
        There isn&rsquo;t enough in-service data for {orgName} this school year to build a
        renewal brief — no district mastery series and no robot with recorded use. Check
        that robots are deployed and syncing, then try another school year.
      </div>
    );
  } else {
    body = (
      <>
        {/* thesis */}
        <p
          style={{
            fontSize: 22,
            lineHeight: 1.42,
            fontWeight: 500,
            color: INK,
            margin: "0 0 40px",
            maxWidth: "44ch",
          }}
        >
          This year, {orgName}&rsquo;s ABii robots helped{" "}
          <b style={{ color: VALUE_COLOR }}>{fmt(pv ? pv.learners : 0)} learners</b> master{" "}
          <b style={{ color: VALUE_COLOR }}>{fmt(pv ? pv.mastered : 0)} standards</b>
          {pv && pv.assisted > 0 && <> — {fmt(pv.assisted)} of them recovered after an initial miss</>}.
          {cap && !cap.degenerate && cap.underCount > 0 && (
            <>
              {" "}But{" "}
              <span style={{ borderBottom: `2px solid ${TIER.low.color}` }}>
                {cap.underCount} of your {robots.length} robots
              </span>{" "}
              ran below what the rest of your fleet already achieves
            </>
          )}
          {renewal && renewal.totalSubs > 0 && (
            <>, and {fmt(renewal.totalSubs)} robot subscriptions come up for renewal</>
          )}
          .
        </p>

        {/* 01 — produced value */}
        {pv && (
          <section style={{ marginBottom: 44 }}>
            <MoveHead num="01" title="What your investment produced" />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 14,
                marginBottom: 20,
              }}
            >
              <Kpi label="Learners engaged" value={fmt(pv.learners)} />
              <Kpi label="Standards mastered" value={fmt(pv.mastered)} color={VALUE_COLOR} />
              {pv.assisted > 0 && (
                <Kpi
                  label="ABii-assisted masteries"
                  value={fmt(pv.assisted)}
                  foot={
                    pv.mastered > 0
                      ? `${Math.round((100 * pv.assisted) / pv.mastered)}% recovered after a miss`
                      : null
                  }
                  color={VALUE_COLOR}
                />
              )}
              {cap && !cap.degenerate && (
                <Kpi label="Lessons completed" value={fmt(cap.lessonsTotal)} />
              )}
            </div>
            <div style={card}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 10,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>
                  Cumulative standards mastered
                </span>
                <span style={eyebrow}>{syLabel(schoolYear)} school year</span>
              </div>
              <div style={{ height: 260 }}>
                <Line
                  data={{
                    labels: pv.series.map((p) => fmtBucketLabel(p.t, "month")),
                    datasets: [
                      {
                        label: "Standards mastered",
                        data: pv.series.map((p) => p.masteredInWindow),
                        borderColor: VALUE_COLOR,
                        backgroundColor: VALUE_COLOR + "22",
                        fill: true,
                        tension: 0.3,
                        pointRadius: 0,
                        borderWidth: 2.5,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false }, datalabels: { display: false } },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: MUTED } },
                      y: {
                        beginAtZero: true,
                        grid: { color: GRID },
                        ticks: { color: MUTED, precision: 0 },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </section>
        )}

        {/* 02 — what's renewing */}
        {renewal && renewal.totalSubs > 0 && (
          <section style={{ marginBottom: 44 }}>
            <MoveHead
              num="02"
              title="What's up for renewal"
              sub={`${fmt(renewal.totalSubs)} robot subscriptions reach term end, grouped by expiration month.`}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 14,
              }}
            >
              {renewal.cohorts.map((c) => (
                <div
                  key={c.key}
                  style={{
                    ...card,
                    borderLeft: `4px solid ${
                      c.expired ? STATUS.critical.color : c.soon ? TIER.low.color : VALUE_COLOR
                    }`,
                  }}
                >
                  <div style={eyebrow}>
                    {c.expired ? "Lapsed" : c.soon ? "Expiring soon" : "Expiring later"}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, ...tnum, margin: "6px 0 4px" }}>
                    {c.n} robot{c.n === 1 ? "" : "s"}
                  </div>
                  <div style={{ fontSize: 13, color: "#52514e" }}>
                    Covering {fmt(c.learners)} learner-seats.
                  </div>
                  <div
                    style={{
                      display: "inline-block",
                      marginTop: 10,
                      fontSize: 12,
                      ...tnum,
                      color: c.expired ? STATUS.critical.color : c.soon ? TIER.low.color : INK,
                      fontWeight: 600,
                    }}
                  >
                    Term ends {c.label}
                    {c.expired ? " (lapsed)" : ""}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 03 — idle capacity */}
        {cap && !cap.degenerate && cap.underCount > 0 && (
          <section style={{ marginBottom: 44 }}>
            <MoveHead
              num="03"
              title="Capacity you've already paid for"
              sub="Every in-service robot ranked by the lessons it delivered this year. The solid line is your fleet's floor — the level three in four of your robots already clear. The tail below it is capacity sitting on shelves."
            />
            <div style={card}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 10,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>
                  Lessons delivered per robot — full fleet, ranked
                </span>
                <span style={eyebrow}>
                  {cap.counts.well} clear the floor · {cap.counts.low} below · {cap.counts.idle} idle
                </span>
              </div>
              <div style={{ height: 280 }}>
                <Bar
                  data={{
                    labels: cap.sorted.map((_, i) => `#${i + 1}`),
                    datasets: [
                      {
                        label: "Lessons delivered",
                        data: cap.sorted.map((r) => r.lessons_total || 0),
                        backgroundColor: cap.sorted.map(
                          (r) => TIER[cap.tierOf(r.lessons_total || 0)].color
                        ),
                        borderWidth: 0,
                        categoryPercentage: 0.92,
                        barPercentage: 0.94,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      datalabels: { display: false },
                      tooltip: {
                        callbacks: {
                          title: (items) => `Robot ${items[0].label}`,
                          label: (item) => `${fmt(item.raw)} lessons this year`,
                        },
                      },
                    },
                    scales: {
                      x: { grid: { display: false }, ticks: { display: false } },
                      y: {
                        beginAtZero: true,
                        grid: { color: GRID },
                        ticks: { color: MUTED, precision: 0 },
                        title: { display: true, text: "lessons / robot", color: MUTED },
                      },
                    },
                  }}
                  plugins={[refLinesPlugin(cap.floor, cap.medianLessons)]}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 18,
                  flexWrap: "wrap",
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: `1px solid ${GRID}`,
                }}
              >
                {["well", "low", "idle"].map((k) => (
                  <span
                    key={k}
                    style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#52514e" }}
                  >
                    <span
                      style={{ width: 11, height: 11, borderRadius: 3, background: TIER[k].color, display: "inline-block" }}
                    />
                    <b style={{ color: INK }}>{TIER[k].label}</b> · {cap.counts[k]} robot
                    {cap.counts[k] === 1 ? "" : "s"}
                  </span>
                ))}
              </div>
            </div>

            {/* projection callout */}
            <div
              style={{
                marginTop: 22,
                background: VALUE_COLOR + "14",
                border: `1px solid ${VALUE_COLOR}55`,
                borderRadius: 14,
                padding: "22px 24px",
              }}
            >
              <p style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4, color: INK, margin: 0, maxWidth: "54ch" }}>
                Your <b>{cap.underCount} under-used robots</b> delivered{" "}
                <b style={{ color: VALUE_COLOR }}>{fmt(cap.underSum)}</b> lessons this year. Lifting
                them to your fleet&rsquo;s floor of <b style={{ color: VALUE_COLOR }}>{cap.floor}</b>{" "}
                lessons each — the level 3 in 4 of your own robots already clear — is about{" "}
                <b style={{ color: VALUE_COLOR }}>{fmt(cap.lessonGap)}</b> more lessons
                {cap.standardsGap > 0 && (
                  <>
                    , which at your district&rsquo;s own rate is roughly{" "}
                    <b style={{ color: VALUE_COLOR }}>{fmt(cap.standardsGap)} more mastered standards</b>
                  </>
                )}{" "}
                from hardware you already own.
              </p>
              <div style={{ display: "flex", gap: 9, marginTop: 14, fontSize: 13, color: "#52514e", maxWidth: "62ch" }}>
                <span style={{ color: VALUE_COLOR, fontWeight: 700 }}>›</span>
                <span>
                  This is a <b>floor, not a target</b> — the level three-quarters of your own fleet
                  already clears, not our usage recommendation. The standards figure applies your
                  district&rsquo;s own mastered-per-lesson rate to the lesson gap.
                </span>
              </div>
            </div>
          </section>
        )}

        {/* recommendation */}
        <section style={{ marginBottom: 24 }}>
          <div style={{ background: INK, color: "#fff", borderRadius: 16, padding: "26px 28px" }}>
            <div style={{ ...eyebrow, color: "rgba(255,255,255,0.6)" }}>The ask</div>
            <h2 style={{ color: "#fff", fontSize: 23, fontWeight: 800, margin: "8px 0 16px" }}>
              Renew the fleet, then put the quiet robots back to work.
            </h2>
            <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 14 }}>
              {renewal && renewal.totalSubs > 0 && (
                <RecoItem n={1}>
                  <b>Renew the {fmt(renewal.totalSubs)} active subscriptions</b> —{" "}
                  {pv ? (
                    <>
                      the fleet returned {fmt(pv.mastered)} mastered standards this year
                      {pv.assisted > 0 && `, ${Math.round((100 * pv.assisted) / pv.mastered)}% recovered from a miss`}
                      . That performance rides on keeping the fleet under contract.
                    </>
                  ) : (
                    "keeping the fleet in service is the baseline for everything below."
                  )}
                </RecoItem>
              )}
              {cap && !cap.degenerate && cap.counts.idle > 0 && (
                <RecoItem n={renewal && renewal.totalSubs > 0 ? 2 : 1}>
                  <b>
                    Redeploy the {cap.counts.idle} idle robot{cap.counts.idle === 1 ? "" : "s"}
                  </b>{" "}
                  — {idleBySchool.length ? topPlaces(idleBySchool) : "spread across the fleet"}. These
                  delivered no lessons this year. Move them to classrooms with demand before term end.
                </RecoItem>
              )}
              {cap && !cap.degenerate && cap.counts.low > 0 && (
                <RecoItem n={(renewal && renewal.totalSubs > 0 ? 1 : 0) + (cap.counts.idle > 0 ? 1 : 0) + 1}>
                  <b>Coach the {cap.counts.low} below-floor classroom{cap.counts.low === 1 ? "" : "s"}</b> —
                  a single PD touch that lifts them to the level three-quarters of your fleet already
                  clears is worth roughly{" "}
                  {cap.standardsGap > 0
                    ? `${fmt(cap.standardsGap)} additional mastered standards`
                    : `${fmt(cap.lessonGap)} additional lessons`}{" "}
                  next year.
                </RecoItem>
              )}
            </ol>
          </div>
        </section>

        <p style={{ marginTop: 8, fontSize: 12, color: MUTED, lineHeight: 1.6, maxWidth: "80ch" }}>
          <b style={{ color: "#52514e" }}>How these numbers are built.</b> Standards mastered counts
          each learner×standard reaching mastered status (outright or ABii-assisted) since the school
          year began; figures reflect the in-service fleet (robots with a current subscription). The{" "}
          <b style={{ color: "#52514e" }}>floor</b> is the 25th percentile of per-robot lessons among
          robots with any use — the level three in four already clear. The redeployment projection
          assumes only that below-floor robots reach that floor, and converts the lesson gap to
          standards using the district&rsquo;s own mastered-per-lesson rate — a deliberately
          conservative within-district estimate, not a target or a guarantee.
        </p>
      </>
    );
  }

  return (
    <MainDashboard>
      <style>{`@media print { .rb-no-print { display: none !important; } .rb-doc { box-shadow: none !important; border: none !important; } }`}</style>
      <div
        className="rb-doc"
        style={{
          padding: 28,
          margin: "8px 4px 24px",
          borderRadius: 16,
          background: "#fff",
          border: `1px solid ${GRID}`,
          boxShadow: "0 2px 10px rgba(43, 42, 40, 0.10)",
        }}
      >
        {/* masthead */}
        <div style={{ borderBottom: `2px solid ${INK}`, paddingBottom: 20, marginBottom: 28 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <div style={{ ...eyebrow, color: VALUE_COLOR, fontWeight: 700 }}>ABii · Renewal Brief</div>
              <h1 style={{ margin: "12px 0 6px", fontSize: 34, color: INK, letterSpacing: "-0.02em" }}>
                {orgName}
              </h1>
              <div style={{ ...eyebrow, color: "#52514e", letterSpacing: "0.04em" }}>
                {syLabel(schoolYear)} school year · prepared {today.toLocaleDateString()}
              </div>
            </div>
            {controls}
          </div>
          <div style={{ marginTop: 14 }} className="rb-no-print">
            <Link
              to={{ pathname: "/admin/insights/fleet", search }}
              style={{ fontSize: 13, fontWeight: 600, color: VALUE_COLOR, textDecoration: "none" }}
            >
              ← Back to Fleet
            </Link>
          </div>
        </div>
        {/* prints with the document on purpose: the brief argues a renewal case
            off these numbers, so the roster caveat belongs in the customer's
            copy, not just on screen */}
        {coverage === "partial" && <CoverageNote text={RECONSTRUCTED_NOTE} />}
        {body}
      </div>
    </MainDashboard>
  );
};

// numbered recommendation row (dark card)
const RecoItem = ({ n, children }) => (
  <li style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
    <span
      style={{
        flex: "none",
        width: 26,
        height: 26,
        borderRadius: "50%",
        background: VALUE_COLOR,
        color: "#fff",
        display: "grid",
        placeItems: "center",
        fontSize: 13,
        fontWeight: 700,
        fontFamily: "ui-monospace, Menlo, monospace",
      }}
    >
      {n}
    </span>
    <span style={{ paddingTop: 2, color: "rgba(255,255,255,0.85)", fontSize: 14, lineHeight: 1.5 }}>
      {children}
    </span>
  </li>
);

// "Lincoln Elementary (3), Roosevelt K-8 (2)" from the idle-by-school list
const topPlaces = (entries) =>
  entries
    .slice(0, 3)
    .map(([name, n]) => `${name} (${n})`)
    .join(", ");

export default withAPI(RenewalBrief);
